export const accordion = { 
  selector: '.v-component--accordion',
  reactive: true,

  run(el, data, root) {
    const headers = el.querySelectorAll('[data-accordion-header]');
    const multiExpand = el.hasAttribute('data-accordion-multi');

    // Screen-reader strings. English defaults keep existing markup working;
    // localised sites override them per instance:
    //   data-accordion-hint="Нажмите Enter или пробел, чтобы раскрыть."
    //   data-accordion-label-expanded="Раздел «{name}» раскрыт."
    //   data-accordion-label-collapsed="Раздел «{name}» свёрнут."
    const srHint = el.getAttribute('data-accordion-hint')
      || 'Press Enter or Space to toggle this section.';
    const srExpanded = el.getAttribute('data-accordion-label-expanded')
      || 'Section "{name}" expanded.';
    const srCollapsed = el.getAttribute('data-accordion-label-collapsed')
      || 'Section "{name}" collapsed.';

    headers.forEach((header, index) => {
      const content = header.nextElementSibling;

      if (!content || !content.hasAttribute('data-accordion-content')) return;

      // IDs
      const headerId = header.id || `accordion-header-${index}`;
      const contentId = content.id || `accordion-content-${index}`;
      header.id = headerId;
      content.id = contentId;

      // ARIA setup
      header.setAttribute('role', 'button');
      header.setAttribute('aria-controls', contentId);
      header.setAttribute('tabindex', '0');
      content.setAttribute('role', 'region');
      content.setAttribute('aria-labelledby', headerId);

      // ARIA Live Region
      let liveRegion = el.querySelector('[aria-live]');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.classList.add('sr-only');
        el.appendChild(liveRegion);
      }

      // Captured before the instructions span is appended — otherwise the
      // announcement reads the hint back as part of the section name.
      const headerName = header.textContent.trim();

      // SR instructions
      const instructions = document.createElement('span');
      instructions.className = 'sr-only';
      instructions.textContent = srHint;
      header.appendChild(instructions);

      // Persistent state key
      const storageKey = `accordion-${contentId}`;
      const storedState = localStorage.getItem(storageKey);
      const attributeState = header.getAttribute('aria-expanded') === 'true' || el.hasAttribute('data-open');
      const initiallyExpanded = storedState === 'open' || (storedState === null && attributeState);

      // Optional
      const closeButton = content.querySelector('[data-close-accordion]');
      const autoCloseDelay = parseInt(content.getAttribute('data-auto-close'), 10);
      const closeOnBlur = content.hasAttribute('data-close-on-blur');
      let autoCloseTimeout = null;

      function updateState(isOpen) {
        header.setAttribute('aria-expanded', String(isOpen));
        el.setAttribute('data-state', isOpen ? 'open' : 'closed');
        localStorage.setItem(storageKey, isOpen ? 'open' : 'closed');

        if (isOpen) {
          content.removeAttribute('hidden');
          liveRegion.textContent = srExpanded.replace('{name}', headerName);
        } else {
          content.setAttribute('hidden', 'true');
          liveRegion.textContent = srCollapsed.replace('{name}', headerName);
        }
      }

      function open() {
        if (!multiExpand) {
          headers.forEach(h => {
            const c = h.nextElementSibling;
            if (c && c.hasAttribute('data-accordion-content')) {
              h.setAttribute('aria-expanded', 'false');
              c.setAttribute('hidden', 'true');
              localStorage.setItem(`accordion-${c.id}`, 'closed');
            }
          });
        }

        updateState(true);

        if (autoCloseDelay) {
          clearTimeout(autoCloseTimeout);
          autoCloseTimeout = setTimeout(close, autoCloseDelay);
        }
      }

      function close() {
        updateState(false);
      }

      function toggle() {
        const isExpanded = header.getAttribute('aria-expanded') === 'true';
        isExpanded ? close() : open();
      }

      // Set initial state
      updateState(initiallyExpanded);

      // Click toggle
      header.addEventListener('click', toggle);

      // Keyboard support
      header.addEventListener('keydown', e => {
        const headersArray = Array.from(headers);
        const i = headersArray.indexOf(header);

        switch (e.key) {
          case 'Enter':
          case ' ':
            toggle();
            e.preventDefault();
            break;
          case 'Escape':
            close();
            break;
          case 'ArrowDown':
            headersArray[(i + 1) % headersArray.length].focus();
            e.preventDefault();
            break;
          case 'ArrowUp':
            headersArray[(i - 1 + headersArray.length) % headersArray.length].focus();
            e.preventDefault();
            break;
          case 'Home':
            headersArray[0].focus();
            e.preventDefault();
            break;
          case 'End':
            headersArray[headersArray.length - 1].focus();
            e.preventDefault();
            break;
        }
      });

      // Internal close button
      if (closeButton) {
        closeButton.addEventListener('click', close);
      }

      // Click outside to close
      if (closeOnBlur) {
        document.addEventListener('click', e => {
          if (!el.contains(e.target)) {
            close();
          }
        });
      }

      // Deep linking
      function openByHash(hash) {
        if (!hash) return;
        if (content.id === hash || header.getAttribute('aria-controls') === hash) {
          open();
          header.focus();
        }
      }

      openByHash(window.location.hash.substring(1));
      window.addEventListener('hashchange', () => {
        openByHash(window.location.hash.substring(1));
      });

      // API exposed on the header element
      header.accordion = { open, close, toggle };
    });
  }
};
